import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, MessageBar, MessageBarBody, Spinner, Text, makeStyles, tokens } from '@fluentui/react-components';
import { useUploadContent, useUploadViewLink } from '../services/conversionsApi';
import { usePageStyles } from '../components/layout/pageStyles';
import { handTabTo } from '../lib/handTabTo';
import { renderPdf } from '../lib/pdfRender';

const useStyles = makeStyles({
  bar: { display: 'flex', gap: tokens.spacingHorizontalM, alignItems: 'center', flexWrap: 'wrap' },
  muted: { color: tokens.colorNeutralForeground3 },
  pages: { display: 'flex', flexDirection: 'column', alignItems: 'center', overflowX: 'auto' },
});

/**
 * View document (2026-10-29): `/uploads/view?clientId=…&documentId=…`, opened from an upload's View link.
 *
 * A PDF is DRAWN IN THIS PAGE with pdf.js, each page with its text layer and a "Page n of N" header (core's ask, so its
 * P2.0 read of the SpotOn report, and a screen reader, see the text). The bytes come from the API, same-origin and
 * behind the gate, so no signed link reaches the page. "Open the original file" (`&raw=1`) hands the tab to a
 * short-lived read-only link to the file itself, which is also what a file that is not a PDF gets. Nothing is stored.
 */
export default function DocumentViewPage() {
  const page = usePageStyles();
  const styles = useStyles();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const clientId = searchParams.get('clientId') ?? '';
  const documentId = searchParams.get('documentId') ?? '';
  const raw = searchParams.get('raw') === '1';

  const link = useUploadViewLink(clientId, documentId, raw);
  const content = useUploadContent(clientId, documentId, !raw);
  const isPdf = !!content.data && /pdf/i.test(content.data.contentType);

  const pagesRef = useRef<HTMLDivElement>(null);
  const [drawn, setDrawn] = useState<{ pages: number } | { error: true } | null>(null);

  useEffect(() => {
    // This screen exists only to hand the tab to the file's own short-lived link.
    if (raw && link.data) handTabTo(link.data.url);
  }, [raw, link.data]);

  useEffect(() => {
    const container = pagesRef.current;
    if (!isPdf || !container || !content.data) return;
    let cancelled = false;
    setDrawn(null);
    renderPdf(container, content.data.bytes, { pageOf: (n, total) => t('documentView.pageOf', { page: n, pages: total }) })
      .then((pages) => { if (!cancelled) setDrawn({ pages }); })
      .catch(() => { if (!cancelled) setDrawn({ error: true }); });
    return () => { cancelled = true; };
  }, [isPdf, content.data, t]);

  const originalHref = `/uploads/view?${new URLSearchParams({ clientId, documentId, raw: '1' }).toString()}`;
  const original = (
    <Button as="a" href={originalHref} target="_blank" rel="noopener noreferrer" size="small">
      {t('documentView.openOriginal')}
    </Button>
  );

  if (!clientId || !documentId) {
    return (
      <div className={page.page}>
        <MessageBar intent="warning"><MessageBarBody>{t('documentView.missing')}</MessageBarBody></MessageBar>
      </div>
    );
  }

  const failure = raw ? link.error : content.error;
  if ((raw ? link.isError : content.isError)) {
    return (
      <div className={page.page}>
        <MessageBar intent="error">
          <MessageBarBody>{failure instanceof Error ? failure.message : t('documentView.failed')}</MessageBarBody>
        </MessageBar>
      </div>
    );
  }

  if (raw) {
    return (
      <div className={page.page}>
        <Spinner label={link.data ? t('documentView.opening', { name: link.data.filename }) : t('documentView.loading')} />
      </div>
    );
  }

  return (
    <div className={page.page}>
      <div className={styles.bar}>
        {!content.data && <Spinner size="tiny" label={t('documentView.loading')} />}
        {isPdf && drawn === null && <Spinner size="tiny" label={t('documentView.drawing')} />}
        {drawn && 'pages' in drawn && <Text className={styles.muted}>{t('documentView.pages', { count: drawn.pages })}</Text>}
        {original}
      </div>
      {content.data && !isPdf && (
        <MessageBar intent="info"><MessageBarBody>{t('documentView.notPdf')}</MessageBarBody></MessageBar>
      )}
      {drawn && 'error' in drawn && (
        <MessageBar intent="warning"><MessageBarBody>{t('documentView.drawFailed')}</MessageBarBody></MessageBar>
      )}
      <div ref={pagesRef} className={styles.pages} />
    </div>
  );
}
